/**
 * App.tsx
 * 
 * SaaS model with business pages and dashboards included
 */

import React, { Suspense } from 'react'
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom'
import { AuthProvider } from './contexts/AuthContext'
import { ChatProvider } from './contexts/ChatContext'
import { AdminProvider } from './contexts/AdminContext'
import ProtectedRoute from './components/ProtectedRoute'
import AdminProtectedRoute from './components/AdminProtectedRoute'
import DevProtectedRoute from './components/DevProtectedRoute'
import Layout from './components/Layout'
import BusinessLayout from './components/BusinessLayout'
import AdminLayout from './components/AdminLayout'
import ChatManager from './components/Chat/ChatManager'
import PageViewTracker from './components/PageViewTracker'

// Lazy load pages for better performance
const Home = React.lazy(() => import('./pages/Home'))
const Demo = React.lazy(() => import('./pages/Demo'))
const Waitlist = React.lazy(() => import('./pages/Waitlist'))
const ContactSales = React.lazy(() => import('./pages/ContactSales'))
const BusinessDashboard = React.lazy(() => import('./pages/BusinessDashboard'))
const ChatbotCreator = React.lazy(() => import('./pages/ChatbotCreator'))
const ChatWithBot = React.lazy(() => import('./pages/ChatWithBot'))
const StreamingChatDemo = React.lazy(() => import('./pages/StreamingChatDemo'))
const EmailConfirmation = React.lazy(() => import('./pages/EmailConfirmation'))
const Login = React.lazy(() => import('./pages/Login'))
const Register = React.lazy(() => import('./pages/Register'))
const Checkout = React.lazy(() => import('./pages/Checkout'))
const TestApi = React.lazy(() => import('./pages/TestApi'))
const AuthCallback = React.lazy(() => import('./pages/AuthCallback'))

// Lazy load BusinessDashboard section pages
const Overview = React.lazy(() => import('./pages/BusinessDashboard/Overview'))
const Team = React.lazy(() => import('./pages/BusinessDashboard/Team'))
const Bots = React.lazy(() => import('./pages/BusinessDashboard/Bots'))
const Analytics = React.lazy(() => import('./pages/BusinessDashboard/Analytics'))
const Integrations = React.lazy(() => import('./pages/BusinessDashboard/Integrations'))
const AIAgent = React.lazy(() => import('./components/AIAgent/AIAgentInterface'))
const AuditLogs = React.lazy(() => import('./pages/BusinessDashboard/AuditLogs'))
const Billing = React.lazy(() => import('./pages/BusinessDashboard/Billing'))
const Compliance = React.lazy(() => import('./pages/BusinessDashboard/Compliance'))
const ProfilePage = React.lazy(() => import('./pages/BusinessDashboard/Profile'))
const SettingsPage = React.lazy(() => import('./pages/BusinessDashboard/Settings'))

// Admin pages
const AdminLogin = React.lazy(() => import('./pages/AdminLogin'))
const AdminDashboard = React.lazy(() => import('./pages/AdminDashboard'))
const AdminUsers = React.lazy(() => import('./pages/AdminUsers'))
const AdminAnalytics = React.lazy(() => import('./pages/AdminAnalytics'))
const AdminFinancials = React.lazy(() => import('./pages/AdminFinancials'))
const AdminChatbots = React.lazy(() => import('./pages/AdminChatbots'))
const AdminSystem = React.lazy(() => import('./pages/AdminSystem'))
const AdminLogs = React.lazy(() => import('./pages/AdminLogs'))





// Loading components with different styles for different contexts
const LoadingSpinner = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
  </div>
)

const BusinessLoadingSpinner = () => (
  <div className="min-h-screen bg-gray-50 flex items-center justify-center">
    <div className="text-center">
      <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary-500 mx-auto mb-4"></div>
      <h2 className="text-xl font-semibold text-gray-900 mb-2">Loading Business Dashboard</h2>
      <p className="text-gray-600">Preparing your enterprise workspace...</p>
    </div>
  </div>
)

const AdminLoadingSpinner = () => (
  <div className="min-h-screen bg-gray-50 flex items-center justify-center">
    <div className="text-center">
      <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-red-500 mx-auto mb-4"></div>
      <h2 className="text-xl font-semibold text-gray-900 mb-2">Loading Admin Panel</h2>
      <p className="text-gray-600">Initializing administrative tools...</p>
    </div>
  </div>
)

// Error Boundary Component
class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error?: Error }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Error caught by boundary:', error, errorInfo)
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="max-w-md w-full bg-white rounded-lg shadow-md p-6">
            <div className="text-center">
              <div className="text-red-500 text-6xl mb-4">⚠️</div>
              <h1 className="text-2xl font-bold text-gray-900 mb-4">
                Something went wrong
              </h1>
              <p className="text-gray-600 mb-6">
                We're sorry, but something unexpected happened. Please try refreshing the page.
              </p>
              <button
                onClick={() => window.location.reload()}
                className="bg-primary-500 text-white px-6 py-2 rounded-lg hover:bg-primary-600 transition-colors"
              >
                Refresh Page
              </button>
            </div>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <Router>
        <AuthProvider>
          <ChatProvider>
            <AdminProvider>
              <ChatManager />
              <PageViewTracker />
              <Suspense fallback={<LoadingSpinner />}>
                <Routes>
                  {/* Public routes - no layout */}
                  <Route path="/" element={<Home />} />
                  <Route path="/demo" element={<Demo />} />
                  <Route path="/waitlist" element={<Waitlist />} />
                  <Route path="/contact-sales" element={<ContactSales />} />
                  <Route path="/login" element={
                    <DevProtectedRoute>
                      <Login />
                    </DevProtectedRoute>
                  } />
                  <Route path="/register" element={
                    <DevProtectedRoute>
                      <Register />
                    </DevProtectedRoute>
                  } />
                  <Route path="/confirm-email" element={<EmailConfirmation />} />
                  <Route path="/test-api" element={<TestApi />} />
                  <Route path="/streaming-demo" element={<StreamingChatDemo />} />
                  <Route path="/auth/callback" element={<AuthCallback />} />
                  
                  {/* Admin routes */}
                  <Route path="/admin/login" element={<AdminLogin />} />
                  <Route
                    path="/admin/dashboard"
                    element={
                      <AdminProtectedRoute>
                        <AdminLayout>
                          <React.Suspense fallback={<AdminLoadingSpinner />}>
                            <AdminDashboard />
                          </React.Suspense>
                        </AdminLayout>
                      </AdminProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/users"
                    element={
                      <AdminProtectedRoute requiredPermission="view_users">
                        <AdminLayout>
                          <React.Suspense fallback={<AdminLoadingSpinner />}>
                            <AdminUsers />
                          </React.Suspense>
                        </AdminLayout>
                      </AdminProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/analytics"
                    element={
                      <AdminProtectedRoute requiredPermission="view_analytics">
                        <AdminLayout>
                          <React.Suspense fallback={<AdminLoadingSpinner />}>
                            <AdminAnalytics />
                          </React.Suspense>
                        </AdminLayout>
                      </AdminProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/financials"
                    element={
                      <AdminProtectedRoute requiredPermission="view_financials">
                        <AdminLayout>
                          <React.Suspense fallback={<AdminLoadingSpinner />}>
                            <AdminFinancials />
                          </React.Suspense>
                        </AdminLayout>
                      </AdminProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/chatbots"
                    element={
                      <AdminProtectedRoute requiredPermission="view_chatbots">
                        <AdminLayout>
                          <React.Suspense fallback={<AdminLoadingSpinner />}>
                            <AdminChatbots />
                          </React.Suspense>
                        </AdminLayout>
                      </AdminProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/system"
                    element={
                      <AdminProtectedRoute requiredPermission="manage_system">
                        <AdminLayout>
                          <React.Suspense fallback={<AdminLoadingSpinner />}>
                            <AdminSystem />
                          </React.Suspense>
                        </AdminLayout>
                      </AdminProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/logs"
                    element={
                      <AdminProtectedRoute requiredPermission="view_logs">
                        <AdminLayout>
                          <React.Suspense fallback={<AdminLoadingSpinner />}>
                            <AdminLogs />
                          </React.Suspense>
                        </AdminLayout>
                      </AdminProtectedRoute>
                    }
                  />
                  
                  {/* Protected routes - with layout */}
                  <Route
                    path="/orgs/:orgId"
                    element={
                      <ProtectedRoute>
                        <BusinessLayout>
                          <React.Suspense fallback={<BusinessLoadingSpinner />}>
                            <Overview />
                          </React.Suspense>
                        </BusinessLayout>
                      </ProtectedRoute>
                    }
                  />
                  {/* Organization-specific routes */}
                  <Route
                    path="/orgs/:orgId/team"
                    element={
                      <ProtectedRoute>
                        <BusinessLayout>
                          <React.Suspense fallback={<BusinessLoadingSpinner />}>
                            <Team />
                          </React.Suspense>
                        </BusinessLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/orgs/:orgId/bots/new"
                    element={
                      <ProtectedRoute>
                        <BusinessLayout>
                          <React.Suspense fallback={<BusinessLoadingSpinner />}>
                            <ChatbotCreator />
                          </React.Suspense>
                        </BusinessLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/orgs/:orgId/bots"
                    element={
                      <ProtectedRoute>
                        <BusinessLayout>
                          <React.Suspense fallback={<BusinessLoadingSpinner />}>
                            <Bots />
                          </React.Suspense>
                        </BusinessLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/orgs/:orgId/analytics"
                    element={
                      <ProtectedRoute>
                        <BusinessLayout>
                          <React.Suspense fallback={<BusinessLoadingSpinner />}>
                            <Analytics />
                          </React.Suspense>
                        </BusinessLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/orgs/:orgId/integrations"
                    element={
                      <ProtectedRoute>
                        <BusinessLayout>
                          <React.Suspense fallback={<BusinessLoadingSpinner />}>
                            <Integrations />
                          </React.Suspense>
                        </BusinessLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/orgs/:orgId/ai-agent"
                    element={
                      <ProtectedRoute>
                        <BusinessLayout>
                          <React.Suspense fallback={<BusinessLoadingSpinner />}>
                            <AIAgent />
                          </React.Suspense>
                        </BusinessLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/orgs/:orgId/audit"
                    element={
                      <ProtectedRoute>
                        <BusinessLayout>
                          <React.Suspense fallback={<BusinessLoadingSpinner />}>
                            <AuditLogs />
                          </React.Suspense>
                        </BusinessLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/orgs/:orgId/billing"
                    element={
                      <ProtectedRoute>
                        <BusinessLayout>
                          <React.Suspense fallback={<BusinessLoadingSpinner />}>
                            <Billing />
                          </React.Suspense>
                        </BusinessLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/orgs/:orgId/compliance"
                    element={
                      <ProtectedRoute>
                        <BusinessLayout>
                          <React.Suspense fallback={<BusinessLoadingSpinner />}>
                            <Compliance />
                          </React.Suspense>
                        </BusinessLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/orgs/:orgId/profile"
                    element={
                      <ProtectedRoute>
                        <BusinessLayout>
                          <React.Suspense fallback={<BusinessLoadingSpinner />}>
                            <ProfilePage />
                          </React.Suspense>
                        </BusinessLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/orgs/:orgId/settings"
                    element={
                      <ProtectedRoute>
                        <BusinessLayout>
                          <React.Suspense fallback={<BusinessLoadingSpinner />}>
                            <SettingsPage />
                          </React.Suspense>
                        </BusinessLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/chat/:botId"
                    element={
                      <ProtectedRoute>
                        <React.Suspense fallback={
                          <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                            <div className="text-center">
                              <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary-500 mx-auto mb-4"></div>
                              <h2 className="text-xl font-semibold text-gray-900 mb-2">Loading Chat Interface</h2>
                              <p className="text-gray-600">Preparing your conversation...</p>
                            </div>
                          </div>
                        }>
                          <ChatWithBot />
                        </React.Suspense>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/checkout/plan/:planId"
                    element={
                      <ProtectedRoute>
                        <Checkout />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/checkout/founder/:planId"
                    element={<Checkout />}
                  />
                </Routes>
              </Suspense>
            </AdminProvider>
          </ChatProvider>
        </AuthProvider>
      </Router>
    </ErrorBoundary>
  )
}

export default App 